import { createSlice } from '@reduxjs/toolkit';

// Функция для загрузки корзины из localStorage
const loadCartFromLocalStorage = () => {
  try {
    const serializedCart = localStorage.getItem('cart');
    if (serializedCart === null) {
      return {
        items: [],
        promoItems: [], // Товары с промокодом
      };
    }
    return JSON.parse(serializedCart);
  } catch (err) {
    // console.error('Error loading cart from localStorage:', err);
    return {
      items: [],
      promoItems: [],
    };
  }
};

// Функция для сохранения корзины в localStorage
const saveCartToLocalStorage = (state) => {
  try {
    const serializedCart = JSON.stringify({
      items: state.items,
      promoItems: state.promoItems,
    });
    localStorage.setItem('cart', serializedCart);
  } catch (err) {
    // console.error('Error saving cart to localStorage:', err);
  }
};

const initialState = {
  ...loadCartFromLocalStorage(),
  orderData: null, // Данные заказа перед отправкой
};

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    // Добавить товар в корзину (обычный)
    addToCart: (state, action) => {
      const {
        itemId,
        itemName,
        itemImage,
        itemPath,
        variant,
        variantName,
        variantIndex,
        price,
        quantity,
      } = action.payload;

      // Проверяем, есть ли уже такой товар с таким вариантом
      const existingItemIndex = state.items.findIndex(
        (item) => item.itemId === itemId && item.variantIndex === variantIndex,
      );

      if (existingItemIndex !== -1) {
        // Увеличиваем количество существующего товара
        state.items[existingItemIndex].quantity += quantity;
      } else {
        // Добавляем новый товар
        state.items.push({
          id: Date.now() + Math.random(), // Уникальный ID для записи в корзине
          itemId,
          itemName,
          itemImage,
          itemPath,
          variant,
          variantName,
          variantIndex,
          price,
          quantity,
          addedAt: new Date().toISOString(),
        });
      }

      saveCartToLocalStorage(state);
    },

    // Добавить товар с промокодом в корзину
    addToCartWithPromo: (state, action) => {
      const {
        itemId,
        itemName,
        itemImage,
        itemPath,
        variant,
        variantName,
        variantIndex,
        price,
        quantity,
        promoCode,
        discountPercent,
      } = action.payload;

      // Вычисляем цену со скидкой
      const discountedPrice = price * (1 - discountPercent / 100);

      // Товары с промокодом всегда добавляются как отдельные записи
      state.promoItems.push({
        id: Date.now() + Math.random(),
        itemId,
        itemName,
        itemImage,
        itemPath,
        variant,
        variantName,
        variantIndex,
        originalPrice: price,
        discountedPrice,
        quantity,
        promoCode,
        discountPercent,
        addedAt: new Date().toISOString(),
      });

      saveCartToLocalStorage(state);
    },

    // Удалить товар из корзины
    removeFromCart: (state, action) => {
      const { id } = action.payload;
      state.items = state.items.filter((item) => item.id !== id);
      saveCartToLocalStorage(state);
    },

    // Удалить товар с промокодом из корзины
    removePromoItemFromCart: (state, action) => {
      const { id } = action.payload;
      state.promoItems = state.promoItems.filter((item) => item.id !== id);
      saveCartToLocalStorage(state);
    },

    // Обновить количество товара
    updateCartItemQuantity: (state, action) => {
      const { id, quantity } = action.payload;
      const item = state.items.find((item) => item.id === id);
      if (item && quantity > 0) {
        item.quantity = quantity;
        saveCartToLocalStorage(state);
      }
    },

    // Обновить количество товара с промокодом
    updatePromoItemQuantity: (state, action) => {
      const { id, quantity } = action.payload;
      const item = state.promoItems.find((item) => item.id === id);
      if (item && quantity > 0) {
        item.quantity = quantity;
        saveCartToLocalStorage(state);
      }
    },

    // Очистить корзину
    clearCart: (state) => {
      state.items = [];
      state.promoItems = [];
      saveCartToLocalStorage(state);
    },

    // Очистить только обычные товары
    clearRegularItems: (state) => {
      state.items = [];
      saveCartToLocalStorage(state);
    },

    // Очистить только товары с промокодом
    clearPromoItems: (state) => {
      state.promoItems = [];
      saveCartToLocalStorage(state);
    },

    // Сохранить данные заказа перед отправкой
    saveOrderData: (state, action) => {
      state.orderData = action.payload;
    },

    // Очистить данные заказа
    clearOrderData: (state) => {
      state.orderData = null;
    },
  },
});

export const {
  addToCart,
  addToCartWithPromo,
  removeFromCart,
  removePromoItemFromCart,
  updateCartItemQuantity,
  updatePromoItemQuantity,
  clearCart,
  clearRegularItems,
  clearPromoItems,
  saveOrderData,
  clearOrderData,
} = cartSlice.actions;

// Селекторы
export const selectCartItems = (state) => state.cart.items;
export const selectPromoItems = (state) => state.cart.promoItems;
export const selectCartTotal = (state) => {
  const regularTotal = state.cart.items.reduce(
    (total, item) => total + item.price * item.quantity,
    0,
  );
  const promoTotal = state.cart.promoItems.reduce(
    (total, item) => total + item.discountedPrice * item.quantity,
    0,
  );
  return regularTotal + promoTotal;
};
export const selectCartItemsCount = (state) => {
  const regularCount = state.cart.items.reduce((count, item) => count + item.quantity, 0);
  const promoCount = state.cart.promoItems.reduce((count, item) => count + item.quantity, 0);
  return regularCount + promoCount;
};
export const selectOrderData = (state) => state.cart.orderData;

export default cartSlice.reducer;

